/*
 * Program: QuillWithMention.jsx
 * Application: Shared Components
 * Option: ReactQuill wrapper with inline @mention dropdown
 * Developer: GF Hotels Dev Team
 * Date: 2026-06-09
 */
import React, { Component, createRef } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import UserService from "../../services/UserService";

const Embed = Quill.import("blots/embed");
const Delta = Quill.import("delta");
class MentionBlot extends Embed {
  static create(value) {
    const node = super.create();
    node.setAttribute("data-slug", value.slug);
    node.setAttribute("data-name", value.name);
    node.setAttribute("contenteditable", "false");
    node.textContent = value.name;
    return node;
  }
  static value(node) {
    return {
      slug: node.getAttribute("data-slug"),
      name: node.getAttribute("data-name"),
    };
  }
}
MentionBlot.blotName = "mention";
MentionBlot.tagName = "span";
MentionBlot.className = "mention-chip";
Quill.register(MentionBlot);

// Clipboard matcher: when stored chip HTML is loaded back into the editor,
// convert <span class="mention-chip" data-slug="..." data-name="..."> into
// a proper mention embed so the chip renders correctly on reload.
function mentionClipboardMatcher(node, delta) {
  const slug = node.getAttribute("data-slug");
  const name = node.getAttribute("data-name");
  if (slug && name) {
    return new Delta().insert({ mention: { slug, name } });
  }
  return delta;
}

class QuillWithMention extends Component {
  constructor(props) {
    super(props);
    this.quillRef = createRef();
    this.wrapperRef = createRef();
    this.state = {
      showDropdown: false,
      dropdownUsers: [],
      dropdownTop: 0,
      dropdownLeft: 0,
      activeIndex: 0,
      mentionQuery: "",
      mentionStart: -1,
      mentionedIds: props.initialMentionedIds || [],
    };
    this._fetchTimer = null;
  }

  // Expose getEditor() so parent can call ref.current.getEditor()
  getEditor() {
    if (this.quillRef.current) {
      return this.quillRef.current.getEditor();
    }
    return null;
  }

  // Expose reset() so parent can clear mention state after save/cancel
  reset() {
    this.setState({
      showDropdown: false,
      dropdownUsers: [],
      activeIndex: 0,
      mentionQuery: "",
      mentionStart: -1,
      mentionedIds: [],
    });
  }

  componentDidUpdate(prevProps) {
    const prev = prevProps.initialMentionedIds || [];
    const next = this.props.initialMentionedIds || [];
    if (JSON.stringify(prev) !== JSON.stringify(next)) {
      this.setState({ mentionedIds: next });
    }
  }

  componentWillUnmount() {
    clearTimeout(this._fetchTimer);
    document.removeEventListener("click", this.handleOutsideClick);
  }

  handleOutsideClick = (e) => {
    if (this.wrapperRef.current && !this.wrapperRef.current.contains(e.target)) {
      this.setState({ showDropdown: false });
    }
  };

  getCaretCoordinates() {
    const quill = this.getEditor();
    if (!quill) return { top: 0, left: 0 };
    const selection = quill.getSelection();
    if (!selection) return { top: 0, left: 0 };
    try {
      const bounds = quill.getBounds(selection.index);
      const editorEl = quill.root.parentElement;
      const editorRect = editorEl ? editorEl.getBoundingClientRect() : { top: 0, left: 0 };
      const wrapperRect = this.wrapperRef.current
        ? this.wrapperRef.current.getBoundingClientRect()
        : { top: 0, left: 0 };
      return {
        top: editorRect.top - wrapperRect.top + bounds.bottom + 4,
        left: editorRect.left - wrapperRect.left + bounds.left,
      };
    } catch {
      return { top: 0, left: 0 };
    }
  }

  // Build the API version of the HTML: replace each mention chip span with @slug.
  // The visual chip stays in the editor; only the payload sees the slug.
  getHtmlWithSlugs(html) {
    if (!html) return html;
    const div = document.createElement("div");
    div.innerHTML = html;
    div.querySelectorAll("span.mention-chip").forEach((span) => {
      const slug = span.getAttribute("data-slug");
      if (slug) {
        span.parentNode.replaceChild(document.createTextNode("@" + slug), span);
      }
    });
    return div.innerHTML;
  }

  handleChange = (value, delta, source, editor) => {
    // Forward the VISUAL HTML (with chip spans) to the parent so ReactQuill's
    // value stays in sync with its own editor state and never replaces the chip
    // with plain text.
    if (this.props.onChange) {
      this.props.onChange(value, delta, source, editor);
    }

    // Separately notify the parent of the API-ready slug version.
    if (this.props.onSlugChange) {
      this.props.onSlugChange(this.getHtmlWithSlugs(value));
    }

    // Only process @-trigger logic for actual user typing.
    if (source !== "user") return;

    const quill = this.getEditor();
    if (!quill) return;
    const sel = quill.getSelection();
    if (!sel) return;
    const cursorIndex = sel.index;
    const textBeforeCursor = quill.getText(0, cursorIndex);
    const atPos = textBeforeCursor.lastIndexOf("@");

    if (atPos === -1) {
      this.setState({ showDropdown: false, mentionStart: -1 });
      return;
    }

    const query = textBeforeCursor.slice(atPos + 1);
    if (query.includes(" ") || query.includes("\n")) {
      this.setState({ showDropdown: false, mentionStart: -1 });
      return;
    }

    const coords = this.getCaretCoordinates();
    this.setState({
      mentionQuery: query,
      mentionStart: atPos,
      dropdownTop: coords.top,
      dropdownLeft: coords.left,
      activeIndex: 0,
    });

    clearTimeout(this._fetchTimer);
    this._fetchTimer = setTimeout(() => this.fetchUsers(query), 200);
    document.addEventListener("click", this.handleOutsideClick);
  };

  async fetchUsers(query) {
    try {
      let qs = "is_dropdown=1";
      if (query) qs += "&search=" + encodeURIComponent(query);
      if (this.props.propertySlug) qs += "&property_slug=" + encodeURIComponent(this.props.propertySlug);
      const res = await UserService.getUserList(qs);
      if (global.successStatus.includes(res.status) && res.data) {
        let users = res.data;
        // Client-side filter: ensures results match the typed query regardless
        // of whether the backend honours the search parameter.
        if (query) {
          const q = query.toLowerCase();
          users = users
            .filter((u) => (u.full_name || "").toLowerCase().includes(q))
            .sort((a, b) => {
              const aName = (a.full_name || "").toLowerCase();
              const bName = (b.full_name || "").toLowerCase();
              const aStarts = aName.startsWith(q);
              const bStarts = bName.startsWith(q);
              if (aStarts && !bStarts) return -1;
              if (!aStarts && bStarts) return 1;
              return aName.localeCompare(bName);
            });
        }
        this.setState({ dropdownUsers: users, showDropdown: users.length > 0 });
      } else {
        this.setState({ showDropdown: false });
      }
    } catch {
      this.setState({ showDropdown: false });
    }
  }

  selectUser = (user) => {
    const quill = this.getEditor();
    if (!quill) return;
    const { mentionStart } = this.state;
    const sel = quill.getSelection();
    if (!sel) return;
    const deleteLength = sel.index - mentionStart;

    // Remove the typed @query text
    quill.deleteText(mentionStart, deleteLength);

    // Insert a mention embed — displays "@Name" as a highlighted chip;
    // getHtmlWithSlugs() will convert it to "@slug" for the API payload.
    quill.insertEmbed(mentionStart, "mention", { slug: user.slug, name: user.full_name }, "user");

    // Insert a trailing space so the cursor lands outside the chip
    quill.insertText(mentionStart + 1, " ", "user");
    quill.setSelection(mentionStart + 2);

    const newIds = [...this.state.mentionedIds, user.slug];
    this.setState({
      showDropdown: false,
      mentionStart: -1,
      mentionQuery: "",
      mentionedIds: newIds,
    });
    if (this.props.onMentionsChange) {
      this.props.onMentionsChange(newIds);
    }
  };

  handleKeyDown = (e) => {
    const { showDropdown, dropdownUsers, activeIndex } = this.state;
    if (!showDropdown) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      this.setState({ activeIndex: Math.min(activeIndex + 1, dropdownUsers.length - 1) });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      this.setState({ activeIndex: Math.max(activeIndex - 1, 0) });
    } else if (e.key === "Enter") {
      if (dropdownUsers[activeIndex]) {
        e.preventDefault();
        this.selectUser(dropdownUsers[activeIndex]);
      }
    } else if (e.key === "Escape") {
      this.setState({ showDropdown: false });
    }
  };

  render() {
    const {
      value, modules, formats, theme, name, className,
      placeholder, readOnly, onChange, onMentionsChange,
      onSlugChange, initialMentionedIds, propertySlug, // consumed here, not forwarded to ReactQuill
      ...rest
    } = this.props;
    const { showDropdown, dropdownUsers, dropdownTop, dropdownLeft, activeIndex } = this.state;

    // Always include 'mention' so Quill preserves the embed blots
    const augmentedFormats = formats ? [...formats, "mention"] : ["mention"];

    // Merge the mention clipboard matcher so stored chip spans are re-parsed
    // as mention embeds when the editor loads existing HTML on open/reload.
    const mergedModules = {
      ...modules,
      clipboard: {
        ...(modules?.clipboard || {}),
        matchers: [
          ...(modules?.clipboard?.matchers || []),
          ["span.mention-chip", mentionClipboardMatcher],
        ],
      },
    };

    return (
      <div
        ref={this.wrapperRef}
        style={{ position: "relative" }}
        onKeyDown={this.handleKeyDown}
      >
        <ReactQuill
          ref={this.quillRef}
          theme={theme || "snow"}
          name={name}
          value={value}
          onChange={this.handleChange}
          modules={mergedModules}
          formats={augmentedFormats}
          className={className}
          placeholder={placeholder}
          readOnly={readOnly}
          {...rest}
        />
        {showDropdown && dropdownUsers.length > 0 && (
          <ul
            style={{
              position: "absolute",
              top: dropdownTop,
              left: dropdownLeft,
              zIndex: 9999,
              background: "#fff",
              border: "1px solid #dee2e6",
              borderRadius: 4,
              boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
              padding: 0,
              margin: 0,
              listStyle: "none",
              minWidth: 200,
              maxHeight: 220,
              overflowY: "auto",
            }}
          >
            {dropdownUsers.map((user, i) => (
              <li
                key={user.slug}
                onMouseDown={(e) => { e.preventDefault(); this.selectUser(user); }}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: i === activeIndex ? "#f0f4ff" : "#fff",
                  fontSize: 14,
                }}
              >
                {user.profile_thumb ? (
                  <img
                    src={user.profile_thumb}
                    alt=""
                    style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                  />
                ) : (
                  <span
                    style={{
                      width: 24, height: 24, borderRadius: "50%",
                      background: "#6c757d", color: "#fff",
                      display: "inline-flex", alignItems: "center",
                      justifyContent: "center", fontSize: 10, flexShrink: 0,
                    }}
                  >
                    {(user.full_name || "").slice(0, 2).toUpperCase()}
                  </span>
                )}
                <span>{user.full_name}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }
}

export default QuillWithMention;
